import type { CardSubject } from "#card-content";

/**
 * The subject overlays a `user_card` read joined. Each is the joined row (only the subject column
 * is needed) or `null` when the card has no overlay of that kind.
 */
export type UserCardSubjectOverlays = {
  userGraphemeMeaningCard: { grapheme: string } | null;
  userGraphemePronunciationCard: { grapheme: string } | null;
  /** Optional: a read that never joins quiz overlays treats every card as a non-quiz. */
  userQuizCard?: { key: string } | null;
  userWordSentenceCard: { word: string } | null;
};

/**
 * What a `user_card` is about, from its actor overlay — decided ONCE here instead of every reader
 * spelling out its own `word ?? pronunciation ?? meaning` chain.
 *
 * A `user_card` carries exactly one overlay, so the order below only matters for a corrupt row; it
 * is fixed (word → pronunciation → meaning) so every reader agrees on it. The answer matches what
 * `cardContentSubject` gives for the resolved content: a grapheme overlay is created FROM its app
 * card's grapheme and a word overlay IS the word (see `createUserCard`).
 *
 * `null` when no overlay has synced yet — each caller decides what an unknown card means for it.
 */
export function resolveUserCardSubject(overlays: UserCardSubjectOverlays): CardSubject | null {
  if (overlays.userWordSentenceCard !== null) {
    return { kind: "word", text: overlays.userWordSentenceCard.word };
  }
  if (overlays.userGraphemePronunciationCard !== null) {
    return { kind: "grapheme", text: overlays.userGraphemePronunciationCard.grapheme };
  }
  if (overlays.userGraphemeMeaningCard !== null) {
    return { kind: "grapheme", text: overlays.userGraphemeMeaningCard.grapheme };
  }
  if (overlays.userQuizCard !== null && overlays.userQuizCard !== undefined) {
    return { kind: "quiz", text: overlays.userQuizCard.key };
  }
  return null;
}
